import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';

/* Wochen-Check-ins der P1-Coaching-Pakete (13.10.2026, gap.md Welle 2): Das Mitglied beantwortet sie in der App, der
   Coach liest hier und antwortet (das Mitglied bekommt einen Push ohne Inhalt). Dieselbe Karte auf /coach und unter
   /coaching im CRM — Aufrufe als Props, beide stabil halten (sonst lädt die Liste bei jedem Zeichnen neu). */

const STATUS = { offen: 'offen', verpasst: 'verpasst', beantwortet: 'beantwortet' };
const datum = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

function Antworten({ fragen, antworten }) {
  return (
    <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[minmax(0,14rem)_1fr]">
      {fragen.filter((f) => antworten?.[f.id] !== null && antworten?.[f.id] !== undefined).map((f) => (
        <div key={f.id} className="contents">
          <dt className="text-xs text-muted-foreground">{f.text}</dt>
          <dd className="break-words">{f.typ === 'skala' ? `${antworten[f.id]} / ${f.max}` : f.typ === 'zahl' ? `${String(antworten[f.id]).replace('.', ',')} ${f.einheit || ''}` : antworten[f.id]}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function CoachingCheckins({ laden, antworten: senden }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [text, setText] = useState({});
  const [busy, setBusy] = useState(null);
  const lade = useCallback(() => {
    laden().then((r) => { setD(r.data); setErr(''); }).catch((e) => setErr(fehlerText(e, 'Check-ins nicht geladen')));
  }, [laden]);
  useEffect(() => { lade(); }, [lade]);

  const antworten = async (c) => {
    setBusy(c.id);
    try { await senden(c.id, text[c.id] || ''); toast.success('Antwort gesendet – das Mitglied sieht sie in der App'); setText((t) => ({ ...t, [c.id]: '' })); lade(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht gesendet')); }
    setBusy(null);
  };

  return (
    <Card className="border-border/60" data-testid="coaching-checkins">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          Wochen-Check-ins {d?.ohne_antwort > 0 && <Badge className="border-transparent bg-primary text-[10px] text-primary-foreground hover:bg-primary">{d.ohne_antwort} warten auf Antwort</Badge>}
        </CardTitle>
        <p className="text-xs text-muted-foreground">Ab der zweiten Woche jede Woche am Wochentag des Starttermins: Schlaf, Energie, Stress, Beschwerden, Gewicht.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {err && <p className="text-sm text-destructive" role="alert">{err}</p>}
        {!d && !err && <Skeleton className="h-20" />}
        {d && !d.checkins.length && <p className="text-sm text-muted-foreground">Noch keine Check-ins – sie entstehen ab der zweiten Woche eines laufenden Pakets.</p>}
        {d && d.checkins.slice(0, 30).map((c) => (
          <div key={c.id} className="space-y-2 border-t border-border/50 pt-3 first:border-t-0 first:pt-0" data-testid={`checkin-${c.id}`}>
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-bold">{c.lead_name}</span>
              <span className="text-muted-foreground">Woche {c.nr} · {datum(c.faellig_am)}</span>
              <Badge variant={c.status === 'beantwortet' ? 'secondary' : 'outline'} className="text-[10px]">{STATUS[c.status] || c.status}</Badge>
            </p>
            {c.status === 'beantwortet' && <Antworten fragen={d.fragen} antworten={c.antworten} />}
            {c.coach_antwort && <p className="rounded-md bg-muted/60 px-3 py-2 text-sm break-words"><span className="text-xs text-muted-foreground">{c.coach_name}: </span>{c.coach_antwort}</p>}
            {c.status === 'beantwortet' && !c.coach_antwort && (
              <div className="flex items-end gap-2">
                <label className="sr-only" htmlFor={`cc-${c.id}`}>Antwort an {c.lead_name}</label>
                <Textarea id={`cc-${c.id}`} rows={1} maxLength={2000} className="min-h-[44px] text-base sm:text-sm" placeholder="Kurz antworten …"
                  value={text[c.id] || ''} onChange={(e) => setText((t) => ({ ...t, [c.id]: e.target.value }))} />
                <Button size="sm" className="h-11" disabled={busy === c.id || (text[c.id] || '').trim().length < 2} onClick={() => antworten(c)}>Senden</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
